/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState } from 'react';
import { LearningPathActionPlan } from '@shared/types/learning';
import { CheckCircle, Circle, AlertCircle, Clock, Play, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { Button } from '@shared/components/ui/button';
import { Progress } from '@shared/components/ui/progress';
import { useToast } from '@shared/components/ui/use-toast';
import { executeActionPlan, usePollExecutionStatus } from '@shared/lib/api/action-plans';
import { ActionPlanStatusResponseData, ExecuteActionPlanResponseData } from '@shared/types/responses/learning';
import { extractActionPlanResults, ActionPlanResults } from '@shared/lib/action-plan-utils';
import { Link } from 'wouter';
import { useChatContext } from '@shared/contexts/ChatContext';

interface PlanListProps {
  plan: LearningPathActionPlan;
  className?: string;
  onExecuteStart?: (executionId: string) => void;
}

// Mapping of entity types to routes based on App.tsx routes
const getEntityRoute = (entityType: string, entityId: number): string | null => {
  switch (entityType.toLowerCase()) {
    case 'topic':
      return `/topics/${entityId}`;
    case 'skill':
      return `/skills/${entityId}`;
    case 'learning_plan':
    case 'plan':
      return `/learning/plans/${entityId}`;
    case 'learning_module':
    case 'module':
      return `/learning/modules/${entityId}`;
    case 'learning_path':
    case 'path':
      return `/learning/paths/${entityId}`;
    case 'skill_graph':
    case 'graph':
      return `/skill-graphs/${entityId}`;
    case 'knowledge_tree':
    case 'tree':
      return `/knowledge-trees/${entityId}`;
    default:
      return null;
  }
};

export function PlanList({ plan, className, onExecuteStart }: PlanListProps) {
  const { state, updateMessageExecutionState } = useChatContext();
  const { toast } = useToast();

  // Find the message containing this action plan
  const message = state.messages.find(msg => msg.actionPlan?.planId === plan.planId);
  const executionState = message?.executionState;

  // Extract execution state values with defaults
  const isExecuting = executionState?.isExecuting || false;
  const isExecuted = executionState?.isExecuted || false;
  const executionId = executionState?.executionId || null;
  const executionStatus = executionState?.executionStatus || null;
  const executionResults = executionState?.executionResults || null;

  // Use the polling hook for execution status
  const { data: pollingStatus, isLoading: isPolling } = usePollExecutionStatus(
    executionId || "",
    {
      enabled: !!executionId && isExecuting,
      intervalMs: 1000,
      onComplete: (status) => {
        if (status.status === 'completed') {
          toast({
            title: "Plan Completed",
            description: `Action plan "${plan.name}" executed successfully!`,
          });
          updateMessageExecutionState(plan.planId, {
            isExecuting: false,
            isExecuted: true
          });
        } else if (status.status === 'failed') {
          toast({
            title: "Plan Failed",
            description: status.error || "Action plan execution failed",
            variant: "destructive"
          });
          updateMessageExecutionState(plan.planId, {
            isExecuting: false
            // Don't set isExecuted to true on failure
          });
        }
      }
    }
  );

  // Update execution status when polling data changes
  React.useEffect(() => {
    if (pollingStatus) {
      updateMessageExecutionState(plan.planId, {
        executionStatus: pollingStatus
      });
    }
  }, [pollingStatus, plan.planId, updateMessageExecutionState]);

  // Fetch execution results when plan completes successfully
  React.useEffect(() => {
    if (isExecuted && executionId && !executionResults) {
      const fetchExecutionResults = async () => {
        try {
          const response = await fetch(`/api/learning/action-plans/executions/${executionId}/results`);
          if (response.ok) {
            const results = await response.json();
            if (results.data) {
              const extractedResults = extractActionPlanResults(results.data);
              updateMessageExecutionState(plan.planId, {
                executionResults: extractedResults
              });
            }
          }
        } catch (error) {
          console.error('Failed to fetch execution results:', error);
        }
      };
      
      fetchExecutionResults();
    }
  }, [isExecuted, executionId, executionResults, plan.planId, updateMessageExecutionState]);

  const getStepStatusIcon = (stepIndex: number) => {
    if (isExecuted) {
      // Show completed status for all steps when plan is executed
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    } else if (isExecuting) {
      // Could show progress for current step, but for now show pending
      return <Circle className="h-4 w-4 text-muted-foreground" />;
    } else {
      // Show pending status for steps that haven't been executed
      return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const handleExecute = async () => {
    updateMessageExecutionState(plan.planId, {
      isExecuting: true,
      executionStatus: null
    });
    
    // Ensure minimum visible duration for the executing state (at least 500ms)
    const minExecutingDuration = 500;
    const executionStartTime = Date.now();
    
    try {
      const result = await executeActionPlan(plan.planId);
      
      // Set execution ID to start polling
      if (result.executionId) {
        updateMessageExecutionState(plan.planId, {
          executionId: result.executionId,
          executionStatus: {
            planId: plan.planId,
            status: 'running',
            progress: 0,
            currentStep: 'Starting execution...'
          }
        });
        
        toast({
          title: "Execution Started",
          description: `Action plan "${plan.name}" is now being executed.`,
        });

        // Notify parent component that execution has started
        if (onExecuteStart) {
          onExecuteStart(result.executionId);
        }
        
        // Ensure minimum duration for executing state visibility
        const elapsedTime = Date.now() - executionStartTime;
        if (elapsedTime < minExecutingDuration) {
          await new Promise(resolve => setTimeout(resolve, minExecutingDuration - elapsedTime));
        }
      } else {
        throw new Error("No execution ID returned from server");
      }
    } catch (error) {
      console.error('Error executing action plan:', error);
      toast({
        title: "Execution Failed",
        description: error instanceof Error ? error.message : "Failed to start action plan execution",
        variant: "destructive",
      });
      updateMessageExecutionState(plan.planId, {
        isExecuting: false
      });
    }
  };

  const getStepDescription = (step: any, index: number) => {
    // Create a readable description from the step
    const action = step.action;
    const target = action.target;
    const actionType = action.actionType;
    
    let description = step.description;
    
    if (!description) {
      // Generate a description based on action type and target
      switch (actionType) {
        case 'create':
          description = `Create ${target.replace('_', ' ')}`;
          break;
        case 'update':
          description = `Update ${target.replace('_', ' ')}`;
          break;
        case 'delete':
          description = `Delete ${target.replace('_', ' ')}`;
          break;
        case 'create_path':
          description = 'Create learning path';
          break;
        case 'add_node':
          description = 'Add node to learning path';
          break;
        case 'add_outcome':
          description = 'Add learning outcome';
          break;
        default:
          description = `${actionType.replace('_', ' ')} ${target.replace('_', ' ')}`;
      }
    }

    // If the plan is executed and we have results, add links to created entities
    if (isExecuted && executionResults) {
      const createdEntityLinks: JSX.Element[] = [];

      // Check for created entities using the extracted results
      // Skills
      executionResults.skills.forEach((skill) => {
        const route = getEntityRoute('skill', skill.id);
        if (route) {
          createdEntityLinks.push(
            <Link key={`skill-${skill.id}`} href={route} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline">
              {skill.name}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
      });

      // Topics
      executionResults.topics.forEach((topic) => {
        const route = getEntityRoute('topic', topic.id);
        if (route) {
          createdEntityLinks.push(
            <Link key={`topic-${topic.id}`} href={route} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline">
              {topic.name}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
      });

      // Learning Plans
      executionResults.learningPlans.forEach((plan) => {
        const route = getEntityRoute('learning_plan', plan.id);
        if (route) {
          createdEntityLinks.push(
            <Link key={`plan-${plan.id}`} href={route} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline">
              {plan.title}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
      });

      // Learning Modules
      executionResults.learningModules.forEach((module) => {
        const route = getEntityRoute('learning_module', module.id);
        if (route) {
          createdEntityLinks.push(
            <Link key={`module-${module.id}`} href={route} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline">
              {module.title}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
      });

      // Learning Paths
      executionResults.learningPaths.forEach((path) => {
        const route = getEntityRoute('learning_path', path.id);
        if (route) {
          createdEntityLinks.push(
            <Link key={`path-${path.id}`} href={route} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline">
              {path.title}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
      });

      // Learning Activities
      executionResults.learningActivities.forEach((activity) => {
        // Note: Activities don't have direct routes in App.tsx, so we skip them for now
      });

      // Learning Outcomes
      executionResults.learningOutcomes.forEach((outcome) => {
        // Note: Outcomes don't have direct routes in App.tsx, so we skip them for now
      });

      // If we found created entities, modify the description
      if (createdEntityLinks.length > 0) {
        return (
          <span>
            {description} - Created: {createdEntityLinks.map((link, idx) => (
              <span key={idx}>
                {idx > 0 && ', '}
                {link}
              </span>
            ))}
          </span>
        );
      }
    }
    
    return description;
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="mb-3">
        <h4 className="font-medium text-sm mb-1">{plan.name}</h4>
        <p className="text-xs text-muted-foreground mb-2">{plan.description}</p>
        <div className="text-xs text-muted-foreground">
          {plan.steps.length} step{plan.steps.length !== 1 ? 's' : ''}
        </div>
      </div>
      
      <div className="space-y-2">
        {plan.steps.map((step, index) => (
          <div 
            key={step.stepId} 
            className="flex items-start gap-3 p-2 rounded-md bg-muted/50 border border-border/50"
          >
            <div className="flex-shrink-0 mt-0.5">
              {getStepStatusIcon(index)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Step {index + 1}
                </span>
              </div>
              <p className="text-sm">{getStepDescription(step, index)}</p>
              {step.dependencies && step.dependencies.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Depends on: {step.dependencies.map(dep => {
                    const depIndex = plan.steps.findIndex(s => s.stepId === dep);
                    return depIndex >= 0 ? `Step ${depIndex + 1}` : dep;
                  }).join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-3 pt-2 border-t border-border/50">
        <div className="text-xs text-muted-foreground mb-3">
          <div className="flex items-center justify-between">
            <span>Plan ID: {plan.planId}</span>
            {plan.metadata.estimatedDurationMs && (
              <span>
                Est. {Math.round(plan.metadata.estimatedDurationMs / 1000 / 60)}min
              </span>
            )}
          </div>
        </div>
        
        {/* Progress Bar and Status */}
        {isExecuting && executionStatus && (
          <div className="mb-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {executionStatus.currentStep || "Processing..."}
              </span>
              <span className="font-medium">{executionStatus.progress || 0}%</span>
            </div>
            <Progress value={executionStatus.progress || 0} className="h-1.5" />
          </div>
        )}
        
        <div className="flex justify-end">
          <Button 
            onClick={handleExecute}
            disabled={isExecuting || isExecuted}
            size="sm"
            className="h-8 px-3"
            variant={isExecuted ? "secondary" : "default"}
          >
            {isExecuting ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Executing...
              </>
            ) : isExecuted ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                Executed
              </>
            ) : (
              <>
                <Play className="h-3 w-3 mr-1" />
                Execute Plan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}